import { useState } from "react";
import { Dialog } from "../design/Dialog";
import { Textfeld } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel, Knopf } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { abweisungsgrund, befehl } from "../plattform/api";
import { useSammlung, type Zeile } from "../plattform/lesemodelle";
import { Leer } from "./teile";

/**
 * Beteiligung (oberflaeche.md §6 Planung, V-24; gemeinsame_drueckjagd/01, F-200, F-219): `dialog` „Beteiligung“ mit den
 * Revieren, die der Veranstalter beteiligt hat (lesemodelle.md §3 „Beteiligungen“), je `article` „<Revier>“ mit dem
 * Beteiligungsstatus. Der Jagdherr des Veranstalters lädt ein — ein vorgeschlagenes Revier (Mitgliedsrevier eines
 * gemeinsamen Jagdherrenkanals, `region` „vorgeschlagene Reviere“) mit `button` „Einladen“ im `article` oder ein
 * anderes über die Revierkennung, die dessen Jagdherr weitergibt (`textbox` „Revierkennung“, `button` „Einladen“; kein
 * Verzeichnis fremder Reviere) — und zieht eine Beteiligung mit `button` „Entfernen“ zurück; das bestätigt der
 * `alertdialog` „Entfernen“ (kein Rückgängig, das Revier verliert sofort jeden Zugriff). Annehmen und Ablehnen gehören
 * dem eingeladenen Revier (Meine Drückjagden, Einladungen). Alle anderen Leser sehen die Reviere nur.
 */
const ZURUECKZIEHBAR = new Set(["eingeladen", "angenommen"]);

const revierId = (z: Zeile) => (z["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? z._id;
const geordnet = (liste: Zeile[]) => [...liste].sort((a, b) => Number(a["_reihenfolge"] ?? 0) - Number(b["_reihenfolge"] ?? 0));

export function BeteiligungDialog({ id, offen, veranstalter, veranstalterName, bearbeitbar, onSchliessen }: {
  id: string; offen: boolean; veranstalter: string | null; veranstalterName: string | null; bearbeitbar: boolean; onSchliessen: () => void;
}) {
  const beteiligungen = useSammlung(offen ? `drueckjagden/${id}/beteiligungen` : null);
  const vorschlaege = useSammlung(offen && bearbeitbar && veranstalter ? `drueckjagden/${id}/reviere/${veranstalter}/vorgeschlagene_reviere` : null);
  const [revierkennung, setRevierkennung] = useState("");
  const [entfernen, setEntfernen] = useState<{ revierId: string; revier: string } | null>(null);
  const [abweisung, setAbweisung] = useState<string | null>(null);

  function schliessen() {
    setRevierkennung(""); setEntfernen(null); setAbweisung(null);
    onSchliessen();
  }

  async function einladen(rv: string) {
    if (!rv.trim()) return;
    setAbweisung(null);
    const a = await befehl("RevierBeteiligen", { "Drückjagd": id, Revier: rv.trim() });
    if (a.ok) setRevierkennung("");
    else setAbweisung(abweisungsgrund(a));
  }

  async function zurueckziehen() {
    const z = entfernen;
    setEntfernen(null);
    if (!z) return;
    const a = await befehl("BeteiligungZurueckziehen", { "Drückjagd": id, Revier: z.revierId });
    setAbweisung(abweisungsgrund(a));
  }

  if (entfernen) {
    return (
      <Dialog name="Entfernen" symbol="schliessen" offen={offen} onSchliessen={() => setEntfernen(null)} bestaetigung>
        <p className="erkl">{`${entfernen.revier} · sofort, kein Rückgängig`}</p>
        <div className="knopfreihe">
          <Knopf onPress={() => void zurueckziehen()}>Bestätigen</Knopf>
          <Knopf art="zweit" onPress={() => setEntfernen(null)}>Abbrechen</Knopf>
        </div>
      </Dialog>
    );
  }

  const liste = geordnet(beteiligungen.daten);
  return (
    <Dialog name="Beteiligung" symbol="link" offen={offen} onSchliessen={schliessen}>
      <div className="formular">
        {veranstalterName ? <p className="eintrag__meta">{`Veranstalter ${veranstalterName}`}</p> : null}
        <section className="liste-bereich" role="region" aria-label="Beteiligte Reviere">
          <h3 className="kopf gruppenkopf platte"><Symbol name="jagdherr" />Reviere</h3>
          {liste.map((z) => {
            const rv = revierId(z);
            const name = String(z["Revier"] ?? rv);
            const titel = `beteiligung-${z._id}`;
            return (
              <article key={z._id} className="eintrag eintrag--knoepfe platte" aria-labelledby={titel}>
                <h4 className="eintrag__t" id={titel}>{name}</h4>
                <span className="eintrag__meta">{String(z["Beteiligungsstatus"] ?? "—")}</span>
                {bearbeitbar && ZURUECKZIEHBAR.has(String(z["Beteiligungsstatus"])) ? (
                  <div className="raster raster--knoepfe">
                    <Kachel symbol="schliessen" begriff="Entfernen" art="kompakt" onPress={() => setEntfernen({ revierId: rv, revier: name })} />
                  </div>
                ) : null}
              </article>
            );
          })}
          {liste.length === 0 && !beteiligungen.laedt ? <Leer text="Keine Beteiligung" symbol="link" /> : null}
        </section>
        {bearbeitbar && vorschlaege.daten.length ? (
          <section className="liste-bereich" role="region" aria-label="vorgeschlagene Reviere">
            <h3 className="kopf gruppenkopf platte"><Symbol name="einladung" />Vorschläge</h3>
            {vorschlaege.daten.map((z) => {
              const titel = `vorschlag-${z._id}`;
              return (
                <article key={z._id} className="eintrag eintrag--knoepfe platte" aria-labelledby={titel}>
                  <h4 className="eintrag__t" id={titel}>{String(z["Revier"] ?? "—")}</h4>
                  <div className="raster raster--knoepfe">
                    <Kachel symbol="einladung" begriff="Einladen" art="kompakt" onPress={() => void einladen(revierId(z))} />
                  </div>
                </article>
              );
            })}
          </section>
        ) : null}
        {bearbeitbar ? (
          <>
            <Textfeld label="Revierkennung" wert={revierkennung} onChange={setRevierkennung} mono />
            <Knopf art="planung-zweit" onPress={() => void einladen(revierkennung)} isDisabled={!revierkennung.trim()}>Einladen</Knopf>
          </>
        ) : null}
        <Abweisung grund={abweisung} />
        <div className="knopfreihe">
          <Knopf art="zweit" onPress={schliessen}>Schließen</Knopf>
        </div>
      </div>
    </Dialog>
  );
}
